import type { DateTime } from "luxon";

const PREVIEW_COUNT = 3;
const DAYS_IN_WEEK = 7;

const sundayOf = (date: DateTime): DateTime => date.minus({ days: date.weekdayISO % 7 });

/**
 * First `count` dates that will be REMOVED when the chosen weekday is dropped from a weekly
 * recurring shift starting at `userStartDate`. Mirrors backend `WeeklyRecurring._calc_future_shifts_internal`:
 * Sunday-anchored cadence — a week is ON when `(weekSunday - originalStartSunday).days % originalInterval == 0`,
 * and the shift fires on `chosenDayOffsetFromSunday` within that week.
 *
 * @param originalStartDate `start_date` of the existing recurring (cadence anchor).
 * @param originalInterval `interval` of the existing recurring (weeks; >=1).
 * @param userStartDate User-selected "delete from" date (clamped to >= originalStartDate).
 * @param chosenDayOffsetFromSunday 0..6 (Sun=0).
 * @param count Number of dates to return.
 */
export function getRemovedShiftDates(
	originalStartDate: DateTime,
	originalInterval: number,
	userStartDate: DateTime,
	chosenDayOffsetFromSunday: number,
	count = PREVIEW_COUNT,
): DateTime[] {
	const effectiveStart = userStartDate < originalStartDate ? originalStartDate : userStartDate;
	const originalSunday = sundayOf(originalStartDate);
	const effectiveSunday = sundayOf(effectiveStart);
	const weekSpan = Math.round(effectiveSunday.diff(originalSunday, "days").days / DAYS_IN_WEEK);

	const candidateInEffectiveWeek = effectiveSunday.plus({ days: chosenDayOffsetFromSunday });
	const remainder = ((weekSpan % originalInterval) + originalInterval) % originalInterval;
	let firstOnWeek: number;
	if (remainder === 0 && candidateInEffectiveWeek >= effectiveStart) {
		firstOnWeek = weekSpan;
	} else if (remainder === 0) {
		firstOnWeek = weekSpan + originalInterval;
	} else {
		firstOnWeek = weekSpan + (originalInterval - remainder);
	}

	const firstRemoved = originalSunday.plus({ days: firstOnWeek * DAYS_IN_WEEK + chosenDayOffsetFromSunday });
	const out: DateTime[] = [];
	for (let i = 0; i < count; i++) {
		out.push(firstRemoved.plus({ days: i * originalInterval * DAYS_IN_WEEK }));
	}
	return out;
}
